import React, { useState, useEffect, useRef } from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import FilterSection from './FilterSection';
import DataOwnerMatrix from './components/DataOwnerMatrix';
import CoverageByModuleChart from './components/CoverageByModuleChart';
import KeyMetricCard from './components/KeyMetricCard';
import ActivitySummaryCard from './components/ActivitySummaryCard';
import ComparativeAnalysis from './components/ComparativeAnalysis';
import DashboardTrendsRow from './components/DashboardTrendsRow';
import UserActivitySection from './components/UserActivitySection';
import AILensButton from './components/AILensButton';
import TeamWorkLoad from '../TeamWorkLoad/TeamWorkLoad';
import AIDashboard from '../AIDashboard/AIDashboard/AIDashboard';
import { useFinancialYear } from "../../context/FinancialYearContext";
import { useDashboardSourceData } from './hooks/useDashboardSourceData';
import { useDashboardFilters } from './hooks/useDashboardFilters';
import { useDashboardSelectors } from './hooks/useDashboardSelectors';
import { useDashboardMetrics } from './hooks/useDashboardMetrics';
import { useActivityFeeds } from './hooks/useActivityFeeds';
import './ESGProgressDashboard.css';

const ESGProgressDashboard = () => {
  const {
    financialYears: financialYear,
    selectedFYId: financialYearId,
    setSelectedFYId: setFinancialYearId,
  } = useFinancialYear();

  const [showAIDashboard, setShowAIDashboard] = useState(false);
  const matrixRef = useRef(null);

  const {
    loading,
    setLoading,
    setError,
    frameworkOptions,
    moduleOptions,
    reportingQuestions,
    reportingAssignDetails,
    reportingAnswers,
    allUsers,
    locations,
    frequency,
  } = useDashboardSourceData(financialYearId);

  const {
    selectedPeriods,
    setSelectedPeriods,
    selectedPeriodsValue,
    selectedLocations,
    setSelectedLocations,
    selectedModules,
    setSelectedModules,
    aggregation,
    setAggregation,
    locationOptions,
    periodOptions,
    handleClearFilters,
  } = useDashboardFilters({ financialYear, financialYearId, locations, moduleOptions, frequency });

  const {
    userResult,
    filteredUsers,
    locationMap,
    mergedFrameworkAnswers,
    filteredQuestionsForProgressTrends,
    filteredQuestionsForProgressLocation,
  } = useDashboardSelectors({
    reportingQuestions,
    allUsers,
    selectedLocations,
    selectedModules,
    selectedPeriods,
    selectedPeriodsValue,
    locationOptions,
    periodOptions,
    reportingAnswers,
  });

  const {
    progressTrendsByLocationData,
    progressTrendsByPeriodsData,
    loadingProgressTrendsByLocation,
    loadingProgressTrendsByPeriods,
    auditorWorkloadData,
    dataOwnerMatrixData,
    loadingDataOwnerMatrix,
    fetchDataOwnerMatrix,
    dashboardData,
  } = useDashboardMetrics({
    financialYearId,
    selectedPeriods,
    selectedPeriodsValue,
    selectedLocations,
    selectedModules,
    aggregation,
    locationOptions,
    periodOptions,
    locations,
    frameworkOptions,
    moduleOptions,
    reportingQuestions,
    reportingAnswers,
    reportingAssignDetails,
    allUsers,
    setLoading,
    setError,
    userResult,
    filteredUsers,
    locationMap,
    mergedFrameworkAnswers,
    filteredQuestionsForProgressTrends,
    filteredQuestionsForProgressLocation,
  });

  const { usersActivities, todaysActivities } = useActivityFeeds({ locationOptions, financialYearId });

  // Load Data Owner Matrix lazily on scroll using IntersectionObserver
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0] && entries[0].isIntersecting) {
          fetchDataOwnerMatrix();
        }
      },
      { rootMargin: '250px' }
    );

    const currentRef = matrixRef.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [fetchDataOwnerMatrix]);

  // Filter handlers — updates the global financial year (drives every tab).
  const handleFinancialYearChange = (event) => {
    setFinancialYearId(event.target.value);
  };

  return (
    <Container fluid className="py-4" style={{ backgroundColor: '#f8f9fa', minHeight: '100vh' }}>
      <FilterSection
        financialYear={financialYear}
        financialYearId={financialYearId}
        onFinancialYearChange={handleFinancialYearChange}
        selectedPeriods={selectedPeriods}
        setSelectedPeriods={setSelectedPeriods}
        selectedLocations={selectedLocations}
        setSelectedLocations={setSelectedLocations}
        locationOptions={locationOptions}
        selectedModules={selectedModules}
        setSelectedModules={setSelectedModules}
        moduleOptions={moduleOptions}
        aggregation={aggregation}
        setAggregation={setAggregation}
        defaultAggregation={frequency}
        onClearFilters={handleClearFilters}
      />

      <KeyMetricCard metrics={dashboardData.metrics} loading={loading} />

      <ActivitySummaryCard activities={dashboardData.monthlyActivities} />

      <DashboardTrendsRow
        progressTrendsByLocationData={progressTrendsByLocationData}
        loadingProgressTrendsByLocation={loadingProgressTrendsByLocation}
        progressTrendsByPeriodsData={progressTrendsByPeriodsData}
        loadingProgressTrendsByPeriods={loadingProgressTrendsByPeriods}
      />

      <Row className="mb-4">
        <Col md={12}>
          <CoverageByModuleChart
            chartData={dashboardData.coverageByModule}
            loading={loading}
          />
        </Col>
      </Row>

      <Row>
        <Col md={12}>
          <TeamWorkLoad user="audit" teamWorkloadData={auditorWorkloadData} />
        </Col>
      </Row>

      <Row className="mt-4" ref={matrixRef}>
        <Col md={12}>
          <DataOwnerMatrix
            matrixData={dataOwnerMatrixData}
            loading={loadingDataOwnerMatrix}
          />
        </Col>
      </Row>

      <ComparativeAnalysis
        financialYear={financialYear}
        moduleOptions={moduleOptions}
        locations={locations}
        reportingQuestions={reportingQuestions}
        reportingAnswers={reportingAnswers}
        allUsers={allUsers}
        periodOptions={periodOptions}
        frequency={aggregation}
        locationOptions={locationOptions}
      />

      <UserActivitySection
        usersActivities={usersActivities}
        todaysActivities={todaysActivities}
      />

      <AILensButton onClick={() => setShowAIDashboard(true)} />

      {showAIDashboard && (
        <AIDashboard
          isOpen={showAIDashboard}
          onClose={() => setShowAIDashboard(false)}
        />
      )}
    </Container>
  );
};

export default ESGProgressDashboard;
